import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  BarChart3,
  Users,
  Settings,
  Bell,
  MessageCircle,
  LogOut,
  ChevronRight,
  Trophy,
  Lock,
  GraduationCap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/features/auth/auth-store";
import { apiGetMe } from "@/features/auth/api";
import { useXpStore } from "@/features/learn/xp-store";
import { BADGE_DEFS, ALL_BADGE_KEYS } from "@/features/learn/badges";
import { getProgress } from "@/features/learn/progress-store";
import { getTotalLessonCount } from "@/features/learn/content";

const Profile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const logout = useAuthStore((s) => s.logout);

  const [stats, setStats] = useState({ handsPlayed: 0, handsWon: 0 });

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    apiGetMe(token)
      .then((data) => {
        updateUser({ chips: data.chips, username: data.username });
        setStats({ handsPlayed: data.handsPlayed, handsWon: data.handsWon });
      })
      .catch((err) => console.warn("Failed to load profile data:", err.message));
  }, [token, navigate, updateUser]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.username
    ? user.username.substring(0, 2).toUpperCase()
    : "??";

  const earnedBadges = useXpStore((s) => s.badges);

  const progress = getProgress();
  const completedLessonCount = Object.values(progress).filter((p) => p.completed).length;
  const allLessonsCompleted = completedLessonCount >= getTotalLessonCount();

  const winRate =
    stats.handsPlayed > 0
      ? Math.round((stats.handsWon / stats.handsPlayed) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-gradient-hero text-white p-6 pt-12">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="w-20 h-20 border-4 border-secondary shadow-glow">
            <AvatarFallback className="bg-secondary text-secondary-foreground text-2xl font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-1">
              {user?.username ?? "Guest"}
            </h1>
            <Badge className="bg-secondary text-secondary-foreground">
              {t("profile_level", {
                level: 1,
                rank: t("profile_rank_strategist"),
              })}
            </Badge>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/[0.08] backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold mb-0.5">{stats.handsPlayed}</p>
            <p className="text-[11px] text-white/60">
              {t("profile_hands_played")}
            </p>
          </div>
          <div className="bg-white/[0.08] backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold mb-0.5 text-accent">{winRate}%</p>
            <p className="text-[11px] text-white/60">
              {t("profile_win_rate")}
            </p>
          </div>
          <div className="bg-white/[0.08] backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-2xl font-bold mb-0.5 text-secondary">
              {user?.chips != null ? user.chips.toLocaleString() : "---"}
            </p>
            <p className="text-[11px] text-white/60">
              {t("profile_total_chips")}
            </p>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-5 max-w-screen-xl mx-auto">
        {/* Menu Items */}
        <section className="space-y-2">
          <Card className="glass p-0 overflow-hidden">
            <Link
              to="/stats"
              className="w-full p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary/15 p-2 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">
                    {t("profile_your_stats")}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("profile_your_stats_desc")}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </Card>

          <Card className="glass p-0 overflow-hidden">
            <Link
              to="/friends"
              className="w-full p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-secondary/15 p-2 rounded-xl">
                  <Users className="w-5 h-5 text-secondary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">
                    {t("profile_friends")}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("profile_friends_desc")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">0</Badge>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </Link>
          </Card>
        </section>

        {/* Badges / Achievements */}
        <section className="space-y-2">
          <h3 className="text-[11px] font-semibold text-muted-foreground mb-2 px-1 tracking-wider uppercase flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5" />
            {t("profile_badges")}
          </h3>
          <Card className="glass p-4">
            <p className="text-xs text-muted-foreground mb-3">{t("profile_badges_desc")}</p>
            <div className="grid grid-cols-3 gap-3">
              {ALL_BADGE_KEYS.map((key) => {
                const badge = BADGE_DEFS[key];
                const earned = earnedBadges.includes(key);
                return (
                  <div
                    key={key}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl ${
                      earned ? "" : "opacity-40 grayscale"
                    }`}
                  >
                    <span className="text-2xl">
                      {earned ? badge.icon : <Lock className="w-5 h-5 text-muted-foreground" />}
                    </span>
                    <p className="text-[10px] text-center leading-tight text-muted-foreground">
                      {i18n.language === "es" ? badge.nameEs : badge.nameEn}
                    </p>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>

        {/* Certificate — only if all lessons completed */}
        {allLessonsCompleted && (
          <section>
            <Card className="glass p-0 overflow-hidden">
              <Link
                to="/certificate"
                className="w-full p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-secondary/15 p-2 rounded-xl">
                    <GraduationCap className="w-5 h-5 text-secondary" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-sm">
                      {t("certificate_title")}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {t("certificate_desc")}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
            </Card>
          </section>
        )}

        {/* Settings */}
        <section>
          <h3 className="text-[11px] font-semibold text-muted-foreground mb-2 px-1 tracking-wider uppercase">
            {t("profile_preferences")}
          </h3>
          <Card className="glass p-0 overflow-hidden divide-y divide-white/[0.06]">
            {[
              { to: "/notifications", icon: Bell, label: t("profile_notifications") },
              { to: "/settings", icon: Settings, label: t("profile_settings") },
              { to: "/support", icon: MessageCircle, label: t("profile_support") },
            ].map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className="w-full p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <p className="font-semibold text-sm">{label}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
            ))}
          </Card>
        </section>

        {/* Logout */}
        <Card className="glass p-0 overflow-hidden">
          <button
            onClick={handleLogout}
            className="w-full p-4 flex items-center justify-center gap-2 hover:bg-destructive/10 transition-colors text-destructive"
          >
            <LogOut className="w-5 h-5" />
            <p className="font-semibold text-sm">{t("profile_logout")}</p>
          </button>
        </Card>

        {/* Footer */}
        <div className="text-center text-[11px] text-muted-foreground space-y-1 pt-4">
          <p>SmartPoker v1.0.0</p>
          <div className="flex items-center justify-center gap-3">
            <a href="/privacy" className="hover:text-foreground transition-colors">
              {t("profile_privacy_policy")}
            </a>
            <span>·</span>
            <a href="/terms" className="hover:text-foreground transition-colors">
              {t("profile_terms_of_service")}
            </a>
            <span>·</span>
            <a href="/cookies" className="hover:text-foreground transition-colors">
              {t("legal_cookies_title")}
            </a>
            <span>·</span>
            <a href="/credits" className="hover:text-foreground transition-colors">
              {t("credits_title")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
